/**
 * 管理员列表管理 Hook
 */
import { useState, useCallback } from "react";
import { Admin } from "../config/types";
import { UNIFIED_API_BASE } from "../../../config/netConfig";

export interface UseAdminListReturn {
  admins: Admin[];
  updateAdminPermissionCount: (adminId: string, count: number) => void;
  loadAdmins: () => Promise<void>;
}

/**
 * 获取管理员权限数量
 */
const getAdminPermissionCount = async (adminId: string): Promise<number> => {
  try {
    const response = await fetch(`${UNIFIED_API_BASE}/admin/${adminId}/all-permissions`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("mslogin_token")}`,
      },
    });
    const data = await response.json();

    if (data.success && data.data?.permissions) {
      // 计算实际的功能权限数量（排除模块节点）
      const count = data.data.permissions.filter((p: any) => p.type !== "MODULE").length;
      console.log(`📊 管理员 ${adminId} 权限数量: ${count}`);
      return count;
    }
    return 0;
  } catch (error) {
    console.error(`获取管理员 ${adminId} 权限数量失败:`, error);
    return 0;
  }
};

/**
 * 管理员列表管理 Hook
 */
export const useAdminList = (): UseAdminListReturn => {
  const [admins, setAdmins] = useState<Admin[]>([]);

  // 更新管理员权限数量的函数
  const updateAdminPermissionCount = useCallback((adminId: string, count: number) => {
    setAdmins((prevAdmins) =>
      prevAdmins.map((admin) => (admin.id === adminId ? { ...admin, permissionCount: count } : admin))
    );
    console.log(`🔄 更新管理员 ${adminId} 权限数量为: ${count}`);
  }, []);

  // 加载管理员列表
  const loadAdmins = useCallback(async () => {
    try {
      // 从统一后端获取管理员列表（优先 /admin/list）
      let response = await fetch(`${UNIFIED_API_BASE}/admin/list`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("mslogin_token")}`,
        },
      });
      let data = await response.json();

      if (data.success && data.data?.items) {
        const adminList: Admin[] = data.data.items.map((admin: any) => ({
          id: admin.id,
          account: admin.admin_account || admin.account,
          name: admin.admin_name || admin.admin_account || admin.account,
          email: admin.email,
          is_super_admin: admin.is_super_admin,
          permissionCount: 0,
        }));

        setAdmins(adminList);
        console.log("📋 加载管理员列表成功:", adminList);

        console.log("🔄 开始获取各管理员权限数量...");
        for (const admin of adminList) {
          const count = await getAdminPermissionCount(admin.id);
          updateAdminPermissionCount(admin.id, count);
        }
        console.log("✅ 所有管理员权限数量获取完成");
        return;
      }

      // 回退到 /roles/admins（无分页）
      console.warn("⚠️ /admin/list 未返回 items，尝试回退到 /roles/admins");
      response = await fetch(`${UNIFIED_API_BASE}/roles/admins`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("mslogin_token")}`,
        },
      });
      data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        const adminList: Admin[] = data.data.map((admin: any) => ({
          id: admin.id,
          account: admin.adminAccount || admin.account || admin.admin_account,
          name: admin.adminAccount || admin.account || admin.admin_account,
          email: admin.email,
          is_super_admin: admin.isSuperAdmin ?? admin.is_super_admin ?? false,
          permissionCount: 0,
        }));
        setAdmins(adminList);
        console.log("📋 通过 /roles/admins 加载管理员列表成功:", adminList);
        for (const admin of adminList) {
          const count = await getAdminPermissionCount(admin.id);
          updateAdminPermissionCount(admin.id, count);
        }
        console.log("✅ 所有管理员权限数量获取完成（回退模式）");
        return;
      }
    } catch (error) {
      console.error("加载管理员列表失败:", error);
      setAdmins([]);
    }
  }, [updateAdminPermissionCount]);

  return {
    admins,
    updateAdminPermissionCount,
    loadAdmins,
  };
};

